/* ----------------------------------------------------------------------------
 * SegmentedNarrator — speaks a briefing/assist reply as an ordered list of
 * PACED segments (owner directive 2026-07-19). The spoken audio breathes
 * EXACTLY like the printed layout: each display section is ONE narration
 * segment [{ text, pauseBeforeMs?, pauseAfterMs }] (a section LABEL spoken as
 * its own short segment, bracketed by pauseBeforeMs/pauseAfterMs, then its
 * content as one continuous chunk). The narrator holds a real, client-timed
 * silence of `pauseBeforeMs` BEFORE and `pauseAfterMs` AFTER each segment (the
 * before-pause defaults to 0 for back-compat / the first segment). The gaps land at paragraph/topic boundaries only — the read is natural
 * and continuous WITHIN a segment (no per-sentence robotic stops), and Aura
 * "breathes" BETWEEN topics.
 *
 * SEQUENTIAL + PREFETCH. Segments play strictly in order (synthesise -> play ->
 * hold the gap -> next). To keep the inter-topic pause from ADDING latency, the
 * NEXT segment's audio is PREFETCHED (synthesised) while the current segment is
 * still playing AND during its gap, so by the time the gap elapses the next
 * clip is already in hand. The default-on TTS cache serves an identical segment
 * for free (keyed per text+voice+speed), so a Replay re-runs the same segments
 * at no provider cost.
 *
 * BARGE-IN is a first-class op — cancel() invalidates the whole run IMMEDIATELY
 * (a new press / Stop / a fresh play() calls it), aborts the in-flight synthesis
 * via cancelSynthesis, and drops every queued/prefetched segment. "speaking"
 * binds to the REAL audio start of the FIRST audible segment (onSpeakStart).
 * The honest-idle contract holds: if NOTHING is ever audible (all synth failed /
 * autoplay-blocked) we report onBlocked, NEVER a silent onEnded — the mobile
 * stuck-in-"thinking" guard.
 *
 * Pure + injectable (no DOM, no real timers): `synthesize`, `play`, `waitForEnd`
 * and `sleep` are injected, so this runs under Node's --test with fakes + a
 * virtual clock — the regression guard for sequential paced playback, the 700ms
 * inter-segment gap, look-ahead prefetch, blocked-idles-honestly and barge-in.
 * -------------------------------------------------------------------------- */

import type { PlayOutcome } from './AuraAudioPlayer.ts';
import type { NarrationSegment } from '../briefing/contract.ts';

export type { NarrationSegment };

export interface SegmentedNarratorDeps {
  /** Synthesize ONE segment's audio bytes (null when nothing / cancelled). */
  synthesize: (text: string) => Promise<Uint8Array | null>;
  /** Play bytes; onStart fires on REAL audio start (binds visible "speaking"). */
  play: (bytes: Uint8Array, onStart: () => void) => Promise<PlayOutcome>;
  /** Resolve when the current clip finishes (natural end / stop). */
  waitForEnd: () => Promise<void>;
  /** Hold a real, client-timed silence between segments (the 700ms gap). When
   *  omitted the gaps collapse to zero (used by callers that never pace). */
  sleep?: (ms: number) => Promise<void>;
  /** Fired on the real audio start of the FIRST audible segment. */
  onSpeakStart?: () => void;
  /** The whole ordered narration finished naturally. */
  onEnded?: () => void;
  /** Nothing could be played (autoplay-blocked / all synth failed): idle honestly. */
  onBlocked?: () => void;
  /** Abort in-flight (and prefetched) synthesis immediately on barge-in. */
  cancelSynthesis?: () => void;
}

/** The speakable segments in order (existing text intact, empties dropped). */
function speakable(segments: NarrationSegment[]): NarrationSegment[] {
  return (segments ?? []).filter((seg) => (seg?.text ?? '').trim() !== '');
}

export class SegmentedNarrator {
  private readonly deps: SegmentedNarratorDeps;
  /** Monotonic run token; a barge-in bumps it so the running play() bails. */
  private run = 0;
  private playing = false;

  constructor(deps: SegmentedNarratorDeps) {
    this.deps = deps;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  /** Barge-in: invalidate the current run + abort in-flight/prefetched synth. */
  cancel(): void {
    this.run += 1;
    this.playing = false;
    this.deps.cancelSynthesis?.();
  }

  /** Synthesize one segment, swallowing errors into a null (nothing audible). */
  private async synth(text: string): Promise<Uint8Array | null> {
    try {
      return await this.deps.synthesize(text);
    } catch {
      return null;
    }
  }

  /**
   * Speak the segments IN ORDER with a real client-timed gap of each segment's
   * `pauseAfterMs` AFTER it (the last segment's 0 => no trailing gap). Prefetch
   * the next segment's audio during the current playback + gap so the pause adds
   * no latency. "speaking" binds to the first audible segment (onSpeakStart).
   * Resolves when the queue finishes OR is cancelled. Never throws.
   */
  async play(segments: NarrationSegment[]): Promise<void> {
    const myRun = ++this.run;
    this.playing = true;

    try {
      const list = speakable(segments);
      if (list.length === 0) {
        // Nothing speakable at all: idle honestly (never a silent onEnded).
        this.playing = false;
        this.deps.onBlocked?.();
        return;
      }

      let spokeAny = false;
      // LOOKAHEAD-2 prefetch (7b fix, voice-wave-4, owner re-test-2 2026-07-27):
      // the FIRST segment synthesises ALONE under "Thinking" (unchanged from
      // the original depth-1 design -- a barge-in during THIS very first synth
      // call still sees exactly one call, preserving that barge-in contract
      // byte-for-byte). From the moment segment 0's bytes are in hand, the
      // queue is topped up to TWO segments' worth of concurrent lookahead
      // (not just one) -- so segment 2 gets a full extra segment's head start
      // it never had under depth-1, hedging against a single slow provider
      // round-trip landing right on a boundary (re-test-2 evidence: a flat
      // ~2s "not synth-ready" overhead at every gap regardless of how long the
      // prior segment played -- see the report for the parts of this that
      // remain unproven offline). Never changes what is spoken, its order, or
      // any other barge-in semantics (the run-token check below is unchanged).
      const pending: Array<Promise<Uint8Array | null>> = [this.synth(list[0].text)];
      let nextToQueue = 1;

      for (let i = 0; i < list.length; i += 1) {
        const bytes = await pending.shift()!;
        if (this.run !== myRun) return; // barged-in while synthesising

        // Top up the queue to depth-2 (two segments ahead of the one about to
        // play); a no-op once nothing is left to prefetch.
        while (pending.length < 2 && nextToQueue < list.length) {
          pending.push(this.synth(list[nextToQueue].text));
          nextToQueue += 1;
        }

        if (!bytes || bytes.byteLength === 0) {
          // This segment produced no audio: skip it and continue the queue
          // (a mid-queue synth hiccup must not strand the rest). No gap for a
          // segment that never spoke.
          continue;
        }

        // Hold the real client-timed silence BEFORE this segment (symmetric to
        // pauseAfterMs). Used to bracket a spoken section LABEL — ~0.5s before it
        // — so Aura "announces" a topic like a chapter heading, then reads its
        // detail. Held only when this segment actually has audio (a failed-synth
        // label leaves no stranded silence); the server zeroes the FIRST
        // segment's before-pause, so there is no leading dead air. The next clip
        // is already prefetching, so this pause is pure breathing, not latency.
        const before = Math.max(0, (list[i].pauseBeforeMs ?? 0) | 0);
        if (before > 0 && this.deps.sleep) {
          await this.deps.sleep(before);
          if (this.run !== myRun) return; // barged-in during the before-pause
        }

        const outcome = await this.deps.play(bytes, () => {
          if (!spokeAny) {
            spokeAny = true;
            this.deps.onSpeakStart?.();
          }
        });
        if (this.run !== myRun) return; // barged-in during playback start
        if (outcome !== 'played') {
          // Autoplay-blocked / error on this clip. Keep needsPlaybackTap (the
          // player surfaces it) and idle honestly: onBlocked (the controller
          // ends 'speaking' if audio had already started, else fails the turn
          // from 'thinking' — never a silent stuck state).
          this.playing = false;
          this.deps.onBlocked?.();
          return;
        }

        await this.deps.waitForEnd();
        if (this.run !== myRun) return; // barged-in during playback

        // Hold the real inter-segment silence BEFORE the next segment (the 700ms
        // paragraph/topic gap). Skipped after the last segment. The next clip is
        // already prefetching, so this pause is pure breathing, not latency.
        if (i < list.length - 1) {
          const gap = Math.max(0, list[i].pauseAfterMs | 0);
          if (gap > 0 && this.deps.sleep) {
            await this.deps.sleep(gap);
            if (this.run !== myRun) return; // barged-in during the gap
          }
        }
      }

      // Whole queue done.
      this.playing = false;
      if (spokeAny) this.deps.onEnded?.();
      else this.deps.onBlocked?.(); // nothing was ever audible
    } finally {
      if (this.run === myRun) this.playing = false;
    }
  }
}
